import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Filter, SlidersHorizontal, X, ChevronDown, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Separator } from '@/components/ui/separator';
import { ProductGrid } from '@/components/products/ProductGrid';
import { useSite } from '@/context/SiteContext';

export const ShopPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { siteData, products, loading, searchProducts, formatPrice } = useSite();
  
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [sortBy, setSortBy] = useState('relevance');
  const [priceRange, setPriceRange] = useState([0, 200]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedThemes, setSelectedThemes] = useState([]);
  const [inStockOnly, setInStockOnly] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Get URL params
  const categoryParam = searchParams.get('categoria');
  const subParam = searchParams.get('sub');
  const filterParam = searchParams.get('filter');
  const searchParam = searchParams.get('search');

  // Calculate max price
  const maxPrice = useMemo(() => {
    if (!products.length) return 200;
    return Math.ceil(Math.max(...products.map(p => p.price)) / 10) * 10;
  }, [products]);

  // Initialize filters from URL
  useEffect(() => {
    if (categoryParam) {
      setSelectedCategories([categoryParam]);
    }
    if (searchParam) {
      setSearchQuery(searchParam);
    }
    setPriceRange([0, maxPrice]);
  }, [categoryParam, searchParam, maxPrice]);

  // Filter and sort products
  useEffect(() => {
    let result = [...products];

    // Search filter
    if (searchQuery || searchParam) {
      const query = searchQuery || searchParam;
      result = searchProducts(query);
    }

    // Category filter
    if (selectedCategories.length > 0) {
      result = result.filter(p => selectedCategories.includes(p.category));
    }

    // Subcategory filter
    if (subParam) {
      result = result.filter(p => p.subcategory === subParam);
    }

    // Special filters
    if (filterParam === 'novidades') {
      result = result.filter(p => p.isNew || p.tags?.includes('novidade'));
    } else if (filterParam === 'mais-vendidos') {
      result = result.filter(p => p.isBestSeller || p.tags?.includes('mais-vendido'));
    }

    // Theme filter
    if (selectedThemes.length > 0) {
      result = result.filter(p => 
        selectedThemes.some(theme => p.tags?.includes(theme) || p.subcategory === theme)
      );
    }

    // Price filter
    result = result.filter(p => p.price >= priceRange[0] && p.price <= priceRange[1]);

    // Stock filter
    if (inStockOnly) {
      result = result.filter(p => p.inStock);
    }

    // Sort
    switch (sortBy) {
      case 'price-asc':
        result.sort((a, b) => a.price - b.price);
        break;
      case 'price-desc':
        result.sort((a, b) => b.price - a.price);
        break;
      case 'newest':
        result.sort((a, b) => (b.isNew ? 1 : 0) - (a.isNew ? 1 : 0));
        break;
      default:
        // relevance - keep original order or prioritize best sellers
        result.sort((a, b) => (b.isBestSeller ? 1 : 0) - (a.isBestSeller ? 1 : 0));
    }

    setFilteredProducts(result);
  }, [products, searchQuery, searchParam, selectedCategories, subParam, filterParam, selectedThemes, priceRange, inStockOnly, sortBy, searchProducts]);

  const toggleCategory = (categoryId) => {
    setSelectedCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(c => c !== categoryId)
        : [...prev, categoryId]
    );
  };

  const toggleTheme = (themeId) => {
    setSelectedThemes(prev => 
      prev.includes(themeId) 
        ? prev.filter(t => t !== themeId)
        : [...prev, themeId]
    );
  };

  const clearFilters = () => {
    setSelectedCategories([]);
    setSelectedThemes([]);
    setPriceRange([0, maxPrice]);
    setInStockOnly(false);
    setSearchQuery('');
    setSearchParams({});
  };

  const hasActiveFilters = selectedCategories.length > 0 || selectedThemes.length > 0 || inStockOnly || searchQuery || priceRange[0] > 0 || priceRange[1] < maxPrice;

  const FilterContent = () => (
    <div className="space-y-6">
      {/* Search */}
      <div>
        <Label className="text-sm font-semibold mb-2 block">Pesquisar</Label>
        <div className="relative">
          <Input
            placeholder="Nome, tema, cor..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        </div>
      </div>

      <Separator />

      {/* Categories */}
      <div>
        <Label className="text-sm font-semibold mb-3 block">Categorias</Label>
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {siteData?.categories?.map((category) => (
            <div key={category.id} className="flex items-center space-x-2">
              <Checkbox
                id={`cat-${category.id}`}
                checked={selectedCategories.includes(category.id)}
                onCheckedChange={() => toggleCategory(category.id)}
              />
              <Label 
                htmlFor={`cat-${category.id}`} 
                className="text-sm cursor-pointer"
              >
                {category.name}
              </Label>
            </div>
          ))}
        </div>
      </div>

      <Separator />

      {/* Themes */}
      <div>
        <Label className="text-sm font-semibold mb-3 block">Temas</Label>
        <div className="flex flex-wrap gap-2">
          {siteData?.featuredThemes?.map((theme) => (
            <Badge
              key={theme.id}
              variant={selectedThemes.includes(theme.id) ? 'default' : 'outline'}
              className="cursor-pointer hover:bg-primary/10"
              onClick={() => toggleTheme(theme.id)}
            >
              {theme.name}
            </Badge>
          ))}
        </div>
      </div>

      <Separator />

      {/* Price Range */}
      <div>
        <Label className="text-sm font-semibold mb-3 block">
          Preço: {formatPrice(priceRange[0])} - {formatPrice(priceRange[1])}
        </Label>
        <Slider
          value={priceRange}
          onValueChange={setPriceRange}
          max={maxPrice}
          step={5}
          className="mt-2"
        />
      </div>

      <Separator />

      {/* Stock */}
      <div className="flex items-center space-x-2">
        <Checkbox
          id="in-stock"
          checked={inStockOnly}
          onCheckedChange={setInStockOnly}
        />
        <Label htmlFor="in-stock" className="text-sm cursor-pointer">
          Apenas produtos em stock
        </Label>
      </div>

      {/* Clear Filters */}
      {hasActiveFilters && (
        <Button 
          variant="outline" 
          className="w-full" 
          onClick={clearFilters}
        >
          <X className="h-4 w-4 mr-2" />
          Limpar Filtros
        </Button>
      )}
    </div>
  );

  // Get page title
  const getPageTitle = () => {
    if (filterParam === 'novidades') return 'Novidades';
    if (filterParam === 'mais-vendidos') return 'Mais Vendidos';
    if (categoryParam) {
      const cat = siteData?.categories?.find(c => c.id === categoryParam);
      return cat?.name || 'Produtos';
    }
    if (searchQuery || searchParam) return `Resultados para "${searchQuery || searchParam}"`;
    return 'Todos os Produtos';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-primary">A carregar...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Breadcrumb */}
      <div className="bg-muted/30 py-4">
        <div className="container mx-auto px-4">
          <nav className="flex items-center gap-2 text-sm text-muted-foreground">
            <Link to="/" className="hover:text-primary transition-colors">Início</Link>
            <span>/</span>
            <span className="text-foreground font-medium">{getPageTitle()}</span>
          </nav>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Desktop Filters */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <div className="sticky top-24 bg-card rounded-2xl p-6 shadow-festa-sm">
              <div className="flex items-center gap-2 mb-6">
                <SlidersHorizontal className="h-5 w-5 text-primary" />
                <h2 className="font-bold text-foreground">Filtros</h2>
              </div>
              <FilterContent />
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-foreground">
                  {getPageTitle()}
                </h1>
                <p className="text-muted-foreground mt-1">
                  {filteredProducts.length} {filteredProducts.length === 1 ? 'produto encontrado' : 'produtos encontrados'}
                </p>
              </div>

              <div className="flex items-center gap-3">
                {/* Mobile Filter Button */}
                <Sheet open={isFilterOpen} onOpenChange={setIsFilterOpen}>
                  <SheetTrigger asChild className="lg:hidden">
                    <Button variant="outline" size="sm">
                      <Filter className="h-4 w-4 mr-2" />
                      Filtros
                      {hasActiveFilters && (
                        <Badge className="ml-2 h-5 w-5 p-0 flex items-center justify-center">
                          !
                        </Badge>
                      )}
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="left" className="w-80">
                    <SheetHeader>
                      <SheetTitle className="flex items-center gap-2">
                        <SlidersHorizontal className="h-5 w-5 text-primary" />
                        Filtros
                      </SheetTitle>
                    </SheetHeader>
                    <div className="mt-6">
                      <FilterContent />
                    </div>
                  </SheetContent>
                </Sheet>

                {/* Sort */}
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Ordenar por" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="relevance">Relevância</SelectItem>
                    <SelectItem value="price-asc">Preço: Menor</SelectItem>
                    <SelectItem value="price-desc">Preço: Maior</SelectItem>
                    <SelectItem value="newest">Novidades</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Active Filters Tags */}
            {hasActiveFilters && (
              <div className="flex flex-wrap gap-2 mb-6">
                {selectedCategories.map(catId => {
                  const cat = siteData?.categories?.find(c => c.id === catId);
                  return cat ? (
                    <Badge 
                      key={catId} 
                      variant="secondary"
                      className="cursor-pointer"
                      onClick={() => toggleCategory(catId)}
                    >
                      {cat.name}
                      <X className="h-3 w-3 ml-1" />
                    </Badge>
                  ) : null;
                })}
                {selectedThemes.map(themeId => {
                  const theme = siteData?.featuredThemes?.find(t => t.id === themeId);
                  return theme ? (
                    <Badge 
                      key={themeId} 
                      variant="secondary"
                      className="cursor-pointer"
                      onClick={() => toggleTheme(themeId)}
                    >
                      {theme.name}
                      <X className="h-3 w-3 ml-1" />
                    </Badge>
                  ) : null;
                })}
                {searchQuery && (
                  <Badge 
                    variant="secondary"
                    className="cursor-pointer"
                    onClick={() => setSearchQuery('')}
                  >
                    "{searchQuery}"
                    <X className="h-3 w-3 ml-1" />
                  </Badge>
                )}
              </div>
            )}

            {/* Products Grid */}
            {filteredProducts.length > 0 ? (
              <ProductGrid products={filteredProducts} columns={4} />
            ) : (
              <div className="text-center py-16">
                <div className="w-20 h-20 mx-auto bg-muted rounded-full flex items-center justify-center mb-4">
                  <Search className="h-10 w-10 text-muted-foreground" />
                </div>
                <h3 className="font-semibold text-foreground mb-2">Nenhum produto encontrado</h3>
                <p className="text-muted-foreground mb-6">
                  Tenta ajustar os filtros ou pesquisar por outro termo.
                </p>
                <Button onClick={clearFilters} className="btn-festa">
                  Limpar Filtros
                </Button>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default ShopPage;
